import {TransactionJournal} from "../managers/TransactionJournal";
import {CHAIN} from "../constants/ChainConstants";
import {maxQueryHeight, validateReadSnapshot} from "../util/ChainProtocol";
import {workUint64} from "../util/WorkDifficulty";
import {SignupRequestDTO} from "../dtos/SignupRequestDTO";
import {ChargeCalculator} from "../util/ChargeCalculator";
import {EVENTS} from "../constants/Events";
import {ChargeLevelChangedEvent} from "../events/ChargeLevelChangedEvent";
import {PLAYER_TYPES} from "../constants/PlayerTypes";
import {WalletManager} from "../managers/WalletManager";
import {PlanetRaid} from "./PlanetRaid";
import {MAP_CONTAINER_IDS, MAP_TYPES} from "../constants/MapConstants";
import {StructTypeCollection} from "./StructTypeCollection";
import {Struct} from "./Struct";
import {StructType} from "./StructType";
import {KeyPlayer} from "./KeyPlayer";
import {StructCountChangedEvent} from "../events/StructCountChangedEvent";
import {PlanetRaidStatusChangedEvent} from "../events/PlanetRaidStatusChangedEvent";
import {Guild} from "./Guild";
import {ActionBarLock} from "./ActionBarLock";
import {Settings} from "./Settings";
import {STRUCT_TYPES} from "../constants/StructConstants";

export class GameState {

  constructor() {
    this.chargeCalculator = new ChargeCalculator();
    this.walletManager = new WalletManager();

    /** @type {GuildAPI} */
    this.guildAPI = null;
    this.alphaRefresh = null;

    /* Multistep Request Data */
    this.signupRequest = new SignupRequestDTO();
    this.transferAmount = 0;

    /**
     * Turned off for simulated sessions (the tutorial) so they never
     * overwrite the player's saved game state.
     *
     * @type {boolean}
     */
    this.persistenceEnabled = true;
    this.sessionBoundary = localStorage.getItem(TransactionJournal.SESSION_CLOSED_KEY);

    /* Persistent Data */
    this.mnemonic = null;
    this.pubkey = null;
    this.lastSaveBlockHeight = 0;
    this.activeMapContainerId = MAP_CONTAINER_IDS.ALPHA_BASE;

    /* Must Be Re-instantiated On Load */
    this.wallet = null;
    this.signingAccount = null;
    this.signingClient = null;

    /** @type {MapComponent} */
    this.alphaBaseMap = null;

    /** @type {MapComponent} */
    this.raidMap = null;

    /** @type {MapComponent} */
    this.previewMap = null;

    /* API Primed Data */

    /** @type {Settings} */
    this.settings = null;

    /** @type {Guild} */
    this.thisGuild = null;

    /**
     * @type {{player: KeyPlayer, raid_enemy: KeyPlayer, planet_raider: KeyPlayer}}
     */
    this.keyPlayers = {
      [PLAYER_TYPES.PLAYER]: new KeyPlayer(
        PLAYER_TYPES.PLAYER,
        true,
        MAP_TYPES.ALPHA_BASE
      ),
      [PLAYER_TYPES.RAID_ENEMY]: new KeyPlayer(
        PLAYER_TYPES.RAID_ENEMY,
        true,
        MAP_TYPES.RAID
      ),
      [PLAYER_TYPES.PLANET_RAIDER]: new KeyPlayer(
        PLAYER_TYPES.PLANET_RAIDER,
        false,
        '',
        PLAYER_TYPES.PLAYER
      )
    };

    this.structTypes = new StructTypeCollection();

    /** @type {Object<string, Struct>} */
    this.previewDefenderStructs = {};

    /** @type {Object<string, Struct>} */
    this.previewAttackerStructs = {};

    /* GRASS Only Data */

    this.currentBlockHeight = 0;

    /**
     * When the chain clock last advanced. Seeded at construction so a staleness
     * check that runs before the first block does not read as stalled.
     *
     * @type {number}
     */
    this.lastBlockAt = Date.now();

    /* Temp Data */

    /**
     * Tracks pending builds before the struct ID is known.
     * Key: "{tileType}-{ambit}-{slot}-{playerId}"
     * Value: {structType: StructType, timestamp: number}
     * @type {Map<string, {structType: StructType, timestamp: number}>}
     */
    this.pendingBuilds = new Map();

    /** @type {ActionBarLock} The current struct action (see STRUCT_ACTIONS) that holds the lock. */
    this.actionBarLock = new ActionBarLock();

    /** @type {AnimationEventQueue} */
    this.animationEventQueue = null;

    /* Allow saving from other classes without cyclical references. */
    window.addEventListener(EVENTS.SAVE_GAME_STATE, this.save.bind(this));
  }

  /**
   * @return {boolean} false once any tab has logged out since this state loaded
   */
  isSessionCurrent() {
    return this.sessionBoundary === localStorage.getItem(TransactionJournal.SESSION_CLOSED_KEY);
  }

  save() {
    if (!this.persistenceEnabled || !this.isSessionCurrent()) {
      return;
    }

    this.lastSaveBlockHeight = this.currentBlockHeight;

    localStorage.setItem('gameState', JSON.stringify({
      mnemonic: this.mnemonic,
      pubkey: this.pubkey,
      thisPlayerId: this.keyPlayers[PLAYER_TYPES.PLAYER].id,
      lastSaveBlockHeight: this.lastSaveBlockHeight,
      lastActionBlockHeight: this.keyPlayers[PLAYER_TYPES.PLAYER].lastActionBlockHeight,
      confirmedLastActionBlockHeight: this.keyPlayers[PLAYER_TYPES.PLAYER].confirmedLastActionBlockHeight,
      planetRaiderLastActionBlockHeight: this.keyPlayers[PLAYER_TYPES.PLANET_RAIDER].lastActionBlockHeight,
      raidEnemyLastActionBlockHeight: this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].lastActionBlockHeight,
      chargeLevel: this.keyPlayers[PLAYER_TYPES.PLAYER].chargeLevel,
      planetRaiderChargeLevel: this.keyPlayers[PLAYER_TYPES.PLANET_RAIDER].chargeLevel,
      raidEnemyChargeLevel: this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].chargeLevel,
      transferAmount: this.transferAmount,
      activeMapContainerId: this.activeMapContainerId
    }));
  }

  async load() {
    const gameState = localStorage.getItem('gameState');

    if (!gameState) {
      return;
    }

    const gameStateParsed = JSON.parse(gameState);

    this.mnemonic = gameStateParsed.mnemonic;
    this.pubkey = gameStateParsed.pubkey;
    this.keyPlayers[PLAYER_TYPES.PLAYER].id = gameStateParsed.thisPlayerId;
    this.lastSaveBlockHeight = gameStateParsed.lastSaveBlockHeight;
    this.keyPlayers[PLAYER_TYPES.PLAYER].lastActionBlockHeight = gameStateParsed.lastActionBlockHeight;
    this.keyPlayers[PLAYER_TYPES.PLAYER].confirmedLastActionBlockHeight = gameStateParsed.confirmedLastActionBlockHeight ?? 0;
    this.keyPlayers[PLAYER_TYPES.PLANET_RAIDER].lastActionBlockHeight = gameStateParsed.planetRaiderLastActionBlockHeight;
    this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].lastActionBlockHeight = gameStateParsed.raidEnemyLastActionBlockHeight;
    this.keyPlayers[PLAYER_TYPES.PLAYER].chargeLevel = gameStateParsed.chargeLevel;
    this.keyPlayers[PLAYER_TYPES.PLANET_RAIDER].chargeLevel = gameStateParsed.planetRaiderChargeLevel;
    this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].chargeLevel = gameStateParsed.raidEnemyChargeLevel;
    this.transferAmount = gameStateParsed.transferAmount;
    this.activeMapContainerId = gameStateParsed.activeMapContainerId;

    if (this.mnemonic) {
      // Properties to re-instantiate
      const wallet = await this.walletManager.createWallet(this.mnemonic);
      const accounts = await wallet.getAccountsWithPrivkeys();
      if (!this.persistenceEnabled || !this.isSessionCurrent()) {
        this.mnemonic = null;
        this.pubkey = null;
        return;
      }
      this.wallet = wallet;
      this.signingAccount = accounts[0];
    }
  }

  /**
   * @param {number} height
   */
  setCurrentBlockHeight(height) {
    this.currentBlockHeight = height;
    this.lastBlockAt = Date.now();

    Object.values(PLAYER_TYPES).forEach(playerType => {
      if (this.keyPlayers[playerType].player) {
        this.keyPlayers[playerType].chargeLevel = this.chargeCalculator.calc(this.currentBlockHeight, this.keyPlayers[playerType].lastActionBlockHeight);

        window.dispatchEvent(new ChargeLevelChangedEvent(this.keyPlayers[playerType].id, this.keyPlayers[playerType].chargeLevel));
      }
      if (this.keyPlayers[playerType].planetUsedForMap && this.keyPlayers[playerType].planetRaidInfo.isRaidActive()) {
        this.keyPlayers[playerType].setPlanetShieldHealth(height);
      }
    });

    this.save();

    console.log(`New Block ${height}`);
  }

  /**
   * Blocks arrive over GRASS every few seconds, which makes this the app's
   * liveness signal: if it goes quiet, the stream is stalled and everything
   * driven by BLOCK_HEIGHT_CHANGED (the signing queue included) has stopped.
   *
   * @return {number}
   */
  msSinceLastBlock() {
    return Date.now() - this.lastBlockAt;
  }

  /**
   * @param {PlanetRaid} info
   * @param dispatchEvent
   */
  setPlanetPlanetRaidInfo(info, dispatchEvent = true) {
    this.keyPlayers[PLAYER_TYPES.PLAYER].planetRaidInfo = info;
    this.keyPlayers[PLAYER_TYPES.PLANET_RAIDER].id = info.fleet_owner;
    this.save();

    if (dispatchEvent) {
      window.dispatchEvent(new PlanetRaidStatusChangedEvent(PLAYER_TYPES.PLAYER));
    }
  }

  /**
   * @param {PlanetRaid} info
   * @param dispatchEvent
   */
  setRaidPlanetRaidInfo(info, dispatchEvent = true) {
    this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].planetRaidInfo = info;
    this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].id = info.planet_owner;
    this.save();

    if (dispatchEvent) {
      window.dispatchEvent(new PlanetRaidStatusChangedEvent(PLAYER_TYPES.RAID_ENEMY));
    }
  }

  /**
   * @param {number} amount
   */
  setTransferAmount(amount) {
    this.transferAmount = amount;
    this.save();
  }

  /**
   * @param {string} id
   */
  setActiveMapContainerId(id) {
    this.activeMapContainerId = id;
    this.save();
  }

  /**
   * @param {array} types
   * @return {boolean} false when the catalog is older than the one already held
   */
  setStructTypes(types) {
    if (this.persistenceEnabled) {
      if (!this.isSessionCurrent()) {
        return false;
      }
      const meta = types?.snapshot_meta;
      validateReadSnapshot(meta, {
        chainId: CHAIN.ID,
        height: maxQueryHeight(meta?.observed_tip, this.currentBlockHeight || meta?.observed_tip),
      });
      const previous = this.structTypes.snapshot_meta;
      if (previous && BigInt(meta.height) < BigInt(previous.height)) {
        return false;
      }
      if (
        previous?.height === meta.height
        && JSON.stringify(types) !== JSON.stringify(this.structTypes.structTypes)
      ) {
        return false;
      }
    }
    if (
      !Array.isArray(types)
      || !types.length
      || new Set(types.map(type => type.id)).size !== types.length
    ) {
      throw new Error('Invalid struct catalog');
    }
    for (const type of types) {
      for (const field of ['build_difficulty', 'ore_mining_difficulty', 'ore_refining_difficulty']) {
        workUint64(type[field]);
      }
    }
    this.structTypes.setStructTypes(types);
    return true;
  }

  /**
   * @param {Struct} struct
   */
  setStruct(struct) {
    const playerType = this.getPlayerTypeById(struct.owner);
    this.keyPlayers[playerType].setStruct(struct);
  }

  /**
   * Removes a struct by ID from all struct objects.
   *
   * @param {string} structId
   * @return {Struct|null} The removed struct, or null if not found
   */
  removeStruct(structId) {
    for (const playerType of Object.values(PLAYER_TYPES)) {
      if (this.keyPlayers[playerType].structs[structId]) {
        const removedStruct = this.keyPlayers[playerType].structs[structId];
        delete this.keyPlayers[playerType].structs[structId];

        window.dispatchEvent(new StructCountChangedEvent(playerType));

        return removedStruct;
      }
    }

    return null;
  }

  /**
   * Adds a pending build to track before the struct ID is known.
   *
   * @param {string} tileType
   * @param {string} ambit
   * @param {number} slot
   * @param {string} playerId
   * @param {StructType} structType
   */
  addPendingBuild(tileType, ambit, slot, playerId, structType) {
    const key = this.getPendingBuildKey(tileType, ambit, slot, playerId);
    this.pendingBuilds.set(key, {
      structType: structType,
      timestamp: Date.now()
    });
  }

  /**
   * Removes a pending build after the struct ID is known.
   *
   * @param {string} tileType
   * @param {string} ambit
   * @param {number} slot
   * @param {string} playerId
   */
  removePendingBuild(tileType, ambit, slot, playerId) {
    const key = this.getPendingBuildKey(tileType, ambit, slot, playerId);
    this.pendingBuilds.delete(key);
  }

  /**
   * @param {Struct[]} structs
   */
  setPreviewDefenderStructs(structs) {
    this.previewDefenderStructs = {};
    structs.forEach(struct => {
      this.previewDefenderStructs[struct.id] = struct;
    });
  }

  /**
   * @param {Struct[]} structs
   */
  setPreviewAttackerStructs(structs) {
    this.previewAttackerStructs = {};
    structs.forEach(struct => {
      this.previewAttackerStructs[struct.id] = struct;
    });
  }

  clearPlanetRaidData() {
    this.keyPlayers[PLAYER_TYPES.PLAYER].planetRaidInfo = new PlanetRaid();
    this.keyPlayers[PLAYER_TYPES.PLANET_RAIDER].id = '';
    this.keyPlayers[PLAYER_TYPES.PLANET_RAIDER].player = null;
    this.keyPlayers[PLAYER_TYPES.PLANET_RAIDER].lastActionBlockHeight = 0;
    this.keyPlayers[PLAYER_TYPES.PLAYER].setPlanetShieldHealth(this.currentBlockHeight);

    this.save();
  }

  clearRaidData() {
    this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].id = '';
    this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].player = null;
    this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].lastActionBlockHeight = 0;
    this.keyPlayers[PLAYER_TYPES.RAID_ENEMY].planet = null;
    this.setRaidPlanetRaidInfo(new PlanetRaid());

    this.save();
  }

  /**
   * @param {string} type player or enemy
   * @return {string|null}
   */
  getPlayerIdByType(type) {
    return (this.keyPlayers[type] && this.keyPlayers[type].id)
      ? this.keyPlayers[type].id
      : null;
  }

  /**
   * @param {String} playerId
   * @return {string}
   */
  getPlayerTypeById(playerId) {
    for (const playerType of Object.values(PLAYER_TYPES)) {
      if (this.keyPlayers[playerType].id === playerId) {
        return playerType;
      }
    }

    throw new Error(`Player with ID ${playerId} has no type`);
  }

  /**
   * Generates a key for the pending builds map.
   *
   * @param {string} tileType
   * @param {string} ambit
   * @param {number} slot
   * @param {string} playerId
   * @return {string}
   */
  getPendingBuildKey(tileType, ambit, slot, playerId) {
    return `${tileType}-${ambit.toLowerCase()}-${slot}-${playerId}`;
  }

  /**
   * Gets a pending build by position.
   *
   * @param {string} tileType
   * @param {string} ambit
   * @param {number} slot
   * @param {string} playerId
   * @return {{structType: StructType, timestamp: number}|null}
   */
  getPendingBuild(tileType, ambit, slot, playerId) {
    const key = this.getPendingBuildKey(tileType, ambit, slot, playerId);
    return this.pendingBuilds.get(key) || null;
  }

  /**
   * @param {string} playerType
   * @return {PlanetRaid}
   */
  getPlanetRaidInfoForKeyPlayer(playerType) {
    if (this.keyPlayers[playerType].hasForeignRaidInfo()) {
      const sourcePlayerType = this.keyPlayers[playerType].getForeignRaidInfoSource();
      return this.keyPlayers[sourcePlayerType].planetRaidInfo;
    }
    return this.keyPlayers[playerType].planetRaidInfo;
  }

  /** @return {null|string} */
  getActiveMapId() {
    const mapContainerElm = document.getElementById(this.activeMapContainerId);
    if (mapContainerElm) {
      const mapElm = mapContainerElm.querySelector('.map');
      if (mapElm) {
        return mapElm.id
      }
    }
    return null;
  }

  /**
   * @param {string|null} playerType
   * @return {Struct|null}
   */
  getPlanetaryDefenseCannonByKeyPlayer(playerType) {
    const keyPlayer = playerType ? this.keyPlayers[playerType] : null;
    const pdcStructType = this.structTypes.getStructType(STRUCT_TYPES.PLANETARY_DEFENSE_CANNON);
    if (!keyPlayer || !pdcStructType) {
      return null;
    }
    return Object.values(keyPlayer.structs).find(struct =>
      struct.type === pdcStructType.id
      && struct.location_type === 'planet'
    ) || null;
  }

  /**
   * @param {string|null} playerType
   * @return {Struct|null}
   */
  getJammingSatelliteByKeyPlayer(playerType) {
    const keyPlayer = playerType ? this.keyPlayers[playerType] : null;
    const jammingSatelliteStructType = this.structTypes.getStructType(STRUCT_TYPES.JAMMING_SATELLITE);
    if (!keyPlayer || !jammingSatelliteStructType) {
      return null;
    }
    return Object.values(keyPlayer.structs).find(struct =>
      struct.type === jammingSatelliteStructType.id
      && struct.location_type === 'planet'
    ) || null;
  }

  printMyPlayer() {
    console.log('Player ID: ' + this.keyPlayers[PLAYER_TYPES.PLAYER].id);
    console.log('Singing Account: ' + this.signingAccount.address);
    console.log('Primary Account: ' + this.keyPlayers[PLAYER_TYPES.PLAYER].player.primary_address);
  }
}
